import { z } from 'zod';
import {
  CONTACT_LIST_DESCRIPTION_MAX,
  contactListFilterSchema,
  orgListReferenceSchema,
  type ContactListFilter,
} from './contact-book';

/**
 * Hub lists (wayai#4931 D6a): one hub's own segments of the org's contact book. A hub list is hub
 * config — held by the hub's HubDO (`hub_contact_list`), in `hub.yaml` as `contact_lists:`,
 * branched preview → production — and is a definition only, never data: a filter over the
 * contacts' labels and scope tags. It has no pins, since a pin is an org contact id and a
 * preview's config would then name production's contacts (D9).
 *
 * WHO (owner decision 1). `hub:write` on the hub manages them, on a preview only: a production
 * hub's lists change through publish, like the rest of its config.
 *
 * MEMBERS. A contact is a member when it carries one of the filter's labels (when the filter names
 * labels) and one of its scope tags (when it names scope tags), and the hub sees it — the org-tag
 * rule of `contact-book.ts`, read against the hub's own tags and environment at each fire (D7). So
 * a hub list never reaches past its hub's visibility, and a preview hub's list never reaches a
 * `production` contact. A filter names at least one label or scope tag: one naming neither would
 * reach no one.
 *
 * REFERENCE. An automation names one by `target: { type: contact_list, list: <name> }`, a name.
 * Renaming a list renames the reference of every automation that names it, unless another list
 * takes the old name in the same write (an automation then keeps the name it states); a list an
 * automation names cannot be deleted. A fire that finds no list of that name records `list_not_found` and raises
 * the hub's alert, as an org list's does.
 */

/** The most lists one hub holds. */
export const HUB_CONTACT_LISTS_MAX = 100;

/** A hub list's name: the slug rule of an org list's, the reference automations and `hub.yaml` carry. */
export const hubListNameSchema = orgListReferenceSchema;

export const HUB_LIST_FILTER_REQUIRED = 'A hub list names at least one label or scope tag';

/** Whether a filter names anything: a hub list's must. */
export function filterNamesAnything(filter: Pick<ContactListFilter, 'labels' | 'scope_tag_ids'>): boolean {
  return filter.labels.length + filter.scope_tag_ids.length > 0;
}

/** A hub list's filter: an org list's filter that names at least one label or scope tag. */
export const hubContactListFilterSchema = contactListFilterSchema.refine(filterNamesAnything, HUB_LIST_FILTER_REQUIRED);

// ---------------------------------------------------------------------------
// Params & queries
// ---------------------------------------------------------------------------

export const hubContactListIdParam = z.object({
  id: z.string().uuid(),
});

export const hubContactListHubQuery = z.object({
  hub_id: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Bodies
// ---------------------------------------------------------------------------

const description = z.string().max(CONTACT_LIST_DESCRIPTION_MAX).nullable().optional();

export const createHubContactListBody = z.object({
  hub_id: z.string().uuid(),
  name: hubListNameSchema,
  description,
  filter: hubContactListFilterSchema,
}).strict();

/** Each field present replaces the stored one. A rename carries the automations' references to the old name (see REFERENCE). */
export const updateHubContactListBody = z.object({
  name: hubListNameSchema.optional(),
  description,
  filter: hubContactListFilterSchema.optional(),
}).strict();

export type CreateHubContactListBody = z.infer<typeof createHubContactListBody>;
export type UpdateHubContactListBody = z.infer<typeof updateHubContactListBody>;
export type CreateHubContactListInput = z.input<typeof createHubContactListBody>;
export type UpdateHubContactListInput = z.input<typeof updateHubContactListBody>;

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

export interface HubContactList {
  list_id: string;
  name: string;
  description: string | null;
  filter: ContactListFilter;
  created_at: string;
  updated_at: string;
}

/**
 * An org tag a hub's list filter may name, as the hub's own reads answer it: the tags the hub
 * carries and the ones its lists' filters already name — never the org's other tags, so a hub-scoped
 * caller learns no tag it does not already hold.
 */
export interface HubListTagOption {
  tag_id: string;
  name: string;
  display_name: string | null;
  color: string | null;
}

export interface ListHubContactListsResponse {
  lists: HubContactList[];
  /** The tags the hub carries and the tags its lists name. */
  tags: HubListTagOption[];
}

export interface HubContactListDetailResponse {
  list: HubContactList;
  /** The names of the hub's automations that target it. */
  automations: string[];
  /** The tags the hub carries and the tags this list names. */
  tags: HubListTagOption[];
}

export interface CreateHubContactListResponse {
  list_id: string;
}

/** Why a hub-list write was refused, as `details.reason` carries it. */
export const HUB_CONTACT_LIST_REFUSALS = [
  'hub_list_name_taken',
  'hub_lists_full',
  'hub_list_in_use',
  'unknown_scope_tags',
] as const;
export type HubContactListRefusal = typeof HUB_CONTACT_LIST_REFUSALS[number];
